import {ChevronDownIcon, ChevronRightIcon} from "lucide-react";
import {Fragment, useCallback, useState} from "react";

import type {VNextComparisonBlock, VNextMonth, VNextRow} from "@hooks/use-vnext";

export interface XeroPnlCellInfo {
    nodeId: string;
    label: string;
    monthKey: string;
    monthLabel: string;
    value: number;
    isForecast: boolean;
    accountCode: string;
}

interface RowVarianceEntry {
    delta: number;
    delta_pct: number | null;
}

interface XeroPnlTableProps {
    pnl: {
        months: VNextMonth[];
        section_totals: Record<string, Record<string, number>>;
        gross_profit: Record<string, number>;
        gross_margin_pct: Record<string, number | null>;
        net_profit: Record<string, number>;
        net_profit_pct: Record<string, number | null>;
        ebitda: Record<string, number>;
        ebitda_margin_pct: Record<string, number | null>;
        comparison: VNextComparisonBlock | null;
        row_variance?: Record<string, RowVarianceEntry> | null;
    };
    onCellClick?: (info: XeroPnlCellInfo) => void;
}

const SECTION_ORDER = ["revenue", "costs", "other", "expenses", "depreciation_amortisation", "interest", "taxes"] as const;
const SECTION_LABELS: Record<string, string> = {
    revenue: "Revenue",
    costs: "Cost of Sales",
    other: "Other Income",
    expenses: "Operating Expenses",
    depreciation_amortisation: "Depreciation and Amortisation",
    interest: "Interest",
    taxes: "Taxes",
};

function sectionKeyForCategory(categoryKey: string): string {
    const cat = categoryKey || "";
    if (cat.startsWith("revenue")) return "revenue";
    if (cat.startsWith("costs") || cat.startsWith("cost.")) return "costs";
    if (cat.startsWith("expense")) return "expenses";
    if (cat.startsWith("depreciation_amortisation")) return "depreciation_amortisation";
    if (cat.startsWith("interest")) return "interest";
    if (cat.startsWith("taxes")) return "taxes";
    return "other";
}

function monthCellClass(m: VNextMonth): string {
    return m.status === "forecast" ? "bg-amber-50/60" : "";
}

function monthDividerClass(months: VNextMonth[], index: number): string {
    const current = months[index];
    const previous = index > 0 ? months[index - 1] : null;
    if (current?.status === "forecast" && previous?.status !== "forecast") {
        return "border-l-2 border-amber-300";
    }
    return "";
}

function fmtCurrency(v: number): string {
    const abs = Math.abs(v);
    const sign = v < 0 ? "-" : "";
    return `${sign}$${abs.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
}

function fmtPct(v: number | null | undefined): string {
    if (v === null || v === undefined) return "—";
    return `${v.toFixed(1)}%`;
}


function VarianceCells({entry}: {entry: RowVarianceEntry | undefined}) {
    if (!entry) {
        return (
            <>
                <td className="text-right px-3 py-1.5 tabular-nums text-muted-foreground">—</td>
                <td className="text-right px-3 py-1.5 tabular-nums text-muted-foreground">—</td>
            </>
        );
    }
    const color = entry.delta > 0 ? "text-green-600" : entry.delta < 0 ? "text-red-600" : "";
    return (
        <>
            <td className={`text-right px-3 py-1.5 tabular-nums font-medium ${color}`}>
                {fmtCurrency(entry.delta)}
            </td>
            <td className={`text-right px-3 py-1.5 tabular-nums ${color}`}>
                {entry.delta_pct != null ? `${entry.delta_pct > 0 ? "+" : ""}${entry.delta_pct}%` : "—"}
            </td>
        </>
    );
}

export function XeroPnlTable({pnl, onCellClick}: XeroPnlTableProps) {
    const {months, section_totals, gross_profit, gross_margin_pct, net_profit, net_profit_pct, ebitda, ebitda_margin_pct, comparison, row_variance} = pnl;
    const hasBackendVariance = !!row_variance && Object.keys(row_variance).length > 0;
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const toggle = useCallback((key: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    if (!months.length) return null;

    // Group rows by section
    const sectionRows: Record<string, Map<string, {row: VNextRow; values: Record<string, number>}>> = {};
    for (const sec of SECTION_ORDER) sectionRows[sec] = new Map();

    for (const m of months) {
        for (const row of m.rows) {
            const sec = sectionKeyForCategory(row.category_key);

            const key = row.node_id;
            if (!sectionRows[sec].has(key)) {
                sectionRows[sec].set(key, {row, values: {}});
            }
            sectionRows[sec].get(key)!.values[m.month_key] = row.value;
        }
    }

    // Filter out all-zero rows
    for (const sec of SECTION_ORDER) {
        for (const [key, entry] of sectionRows[sec]) {
            if (Object.values(entry.values).every((v) => Math.abs(v) < 0.01)) {
                sectionRows[sec].delete(key);
            }
        }
    }

    // Build comparison data (parallel structure)
    let compSectionTotals: Record<string, number> | null = null;
    let compGrossTotal: number | null = null;
    let compNetTotal: number | null = null;
    let compEbitdaTotal: number | null = null;
    let compRowTotals: Record<string, number> | null = null;

    const hasComparison = !!comparison?.months?.length;

    if (hasComparison) {
        compSectionTotals = {};
        compRowTotals = {};

        // Build comparison row totals by node_id
        for (const m of comparison!.months) {
            for (const row of m.rows) {
                compRowTotals[row.node_id] = (compRowTotals[row.node_id] ?? 0) + row.value;
            }
        }

        // Build comparison section totals
        for (const sec of SECTION_ORDER) {
            let secTotal = 0;
            for (const [nodeId] of sectionRows[sec]) {
                secTotal += compRowTotals[nodeId] ?? 0;
            }
            compSectionTotals[sec] = secTotal;
        }

        // Comparison gross & net totals
        const compRevenueTotal = compSectionTotals["revenue"] ?? 0;
        const compCostsTotal = compSectionTotals["costs"] ?? 0;
        compGrossTotal = compRevenueTotal - Math.abs(compCostsTotal);
        const compExpensesTotal = compSectionTotals["expenses"] ?? 0;
        const compOtherTotal = compSectionTotals["other"] ?? 0;
        const compDepreciationTotal = compSectionTotals["depreciation_amortisation"] ?? 0;
        const compInterestTotal = compSectionTotals["interest"] ?? 0;
        const compTaxTotal = compSectionTotals["taxes"] ?? 0;
        compEbitdaTotal = compGrossTotal + compOtherTotal - Math.abs(compExpensesTotal);
        compNetTotal = compEbitdaTotal - Math.abs(compDepreciationTotal) - Math.abs(compInterestTotal) - Math.abs(compTaxTotal);
    }

    // Show variance columns whenever comparison is active (backend or computed)
    const hasVariance = hasBackendVariance || hasComparison;

    const primaryGrossTotal = Object.values(gross_profit).reduce((s, v) => s + v, 0);
    const primaryNetTotal = Object.values(net_profit).reduce((s, v) => s + v, 0);
    const primaryEbitdaTotal = ebitda ? Object.values(ebitda).reduce((s, v) => s + v, 0) : 0;
    const primaryRevenueTotal = Object.values(section_totals["revenue"] || {}).reduce((s, v) => s + v, 0);

    // Compute total percentages from totals (weighted average, not average of monthly %)
    const totalGrossMarginPct = primaryRevenueTotal ? (primaryGrossTotal / primaryRevenueTotal) * 100 : null;
    const totalNetProfitPct = primaryRevenueTotal ? (primaryNetTotal / primaryRevenueTotal) * 100 : null;
    const totalEbitdaMarginPct = primaryRevenueTotal ? (primaryEbitdaTotal / primaryRevenueTotal) * 100 : null;
    const hasSectionData = (sec: string) => {
        const rows = sectionRows[sec];
        return (rows && rows.size > 0) || Object.values(section_totals[sec] || {}).some((v) => v !== 0);
    };
    const renderSection = (sec: string) => {
        if (!hasSectionData(sec)) return null;
        const rows = Array.from(sectionRows[sec].values());
        const secTotal = Object.values(section_totals[sec] || {}).reduce((s, v) => s + v, 0);
        return (
            <SectionBlock
                key={sec}
                label={SECTION_LABELS[sec]}
                rows={rows}
                months={months}
                totals={section_totals[sec] || {}}
                onCellClick={onCellClick}
                compRowTotals={compRowTotals}
                compSectionTotal={compSectionTotals?.[sec] ?? null}
                sectionTotal={secTotal}
                rowVariance={row_variance}
                sectionKey={sec}
                hasVariance={hasVariance}
                hasComparison={hasComparison}
                expanded={expanded}
                onToggle={toggle}
            />
        );
    };

    return (
        <div>
            <div className="flex items-baseline gap-3 mb-3">
                <h3 className="text-sm font-bold uppercase tracking-[2px] text-[#209DB3]">
                    Profit & Loss Statement
                </h3>
                {hasComparison && (
                    <span className="text-[11px] text-muted-foreground">
                        vs {comparison!.period_label}
                    </span>
                )}
            </div>
            <div className="overflow-x-auto border rounded-lg">
                <table className="text-xs w-full" style={{minWidth: `${200 + (months.length + 1 + (hasComparison ? 1 : 0) + (hasVariance ? 2 : 0)) * 100}px`}}>
                    <thead>
                        <tr className="bg-[#209DB3] text-white">
                            <th className="text-left px-3 py-2 font-medium sticky left-0 bg-[#209DB3] min-w-[200px]">
                                Account
                            </th>
                            {months.map((m, i) => (
                                <th
                                    key={m.month_key}
                                    className={`text-right px-3 py-2 font-medium min-w-[90px] ${m.status === "forecast" ? "bg-[#1A8C9E]" : ""} ${monthDividerClass(months, i)}`}
                                >
                                    {m.label || m.month_key}
                                    {m.status === "forecast" && (
                                        <span className="ml-1 rounded bg-white/15 px-1 py-0.5 text-[9px] font-semibold tracking-wide text-white/90">
                                            F
                                        </span>
                                    )}
                                </th>
                            ))}
                            <th className="text-right px-3 py-2 font-bold min-w-[100px]">Total</th>
                            {hasComparison && (
                                <th className="text-right px-3 py-2 font-bold min-w-[100px] bg-[#1A8C9E]">
                                    {comparison!.period_label}
                                </th>
                            )}
                            {hasVariance && (
                                <>
                                    <th className="text-right px-3 py-2 font-medium min-w-[90px]">$ Var</th>
                                    <th className="text-right px-3 py-2 font-medium min-w-[80px]">% Var</th>
                                </>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {renderSection("revenue")}
                        {renderSection("costs")}

                        {/* Gross Profit */}
                        <SummaryRow
                            label="Gross Profit"
                            values={gross_profit}
                            months={months}
                            className="bg-[#5BF6C8]/20 font-bold"
                            compTotal={compGrossTotal}
                            primaryTotal={primaryGrossTotal}
                            varianceEntry={row_variance?.["gross_profit"]}
                            hasVariance={hasVariance}
                            hasComparison={hasComparison}
                        />

                        {/* Gross Margin % */}
                        {gross_margin_pct && (
                            <PctRow
                                label="Gross Margin %"
                                values={gross_margin_pct}
                                months={months}
                                totalPct={totalGrossMarginPct}
                                hasVariance={hasVariance}
                                hasComparison={hasComparison}
                            />
                        )}

                        {renderSection("other")}
                        {renderSection("expenses")}

                        {/* EBITDA — only when the Xero snapshot exposes it natively */}
                        {ebitda && Object.keys(ebitda).length > 0 && (
                            <SummaryRow
                                label="EBITDA"
                                values={ebitda}
                                months={months}
                                className="bg-amber-50 font-bold"
                                compTotal={compEbitdaTotal}
                                primaryTotal={primaryEbitdaTotal}
                                varianceEntry={row_variance?.["ebitda"]}
                                hasVariance={hasVariance}
                                hasComparison={hasComparison}
                            />
                        )}

                        {/* EBITDA Margin % — only when EBITDA is native */}
                        {ebitda_margin_pct && Object.keys(ebitda_margin_pct).length > 0 && (
                            <PctRow
                                label="EBITDA Margin %"
                                values={ebitda_margin_pct}
                                months={months}
                                totalPct={totalEbitdaMarginPct}
                                hasVariance={hasVariance}
                                hasComparison={hasComparison}
                            />
                        )}

                        {renderSection("depreciation_amortisation")}
                        {renderSection("interest")}
                        {renderSection("taxes")}

                        {/* Net Profit */}
                        <SummaryRow
                            label="Net Profit"
                            values={net_profit}
                            months={months}
                            className="bg-[#209DB3]/10 font-bold"
                            compTotal={compNetTotal}
                            primaryTotal={primaryNetTotal}
                            varianceEntry={row_variance?.["net_profit"]}
                            hasVariance={hasVariance}
                            hasComparison={hasComparison}
                        />

                        {/* Net Profit % */}
                        {net_profit_pct && (
                            <PctRow
                                label="Net Profit %"
                                values={net_profit_pct}
                                months={months}
                                totalPct={totalNetProfitPct}
                                hasVariance={hasVariance}
                                hasComparison={hasComparison}
                            />
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function computeVariance(current: number, comparison: number): RowVarianceEntry {
    const delta = current - comparison;
    const delta_pct = comparison !== 0 ? ((current - comparison) / Math.abs(comparison)) * 100 : (current === 0 ? null : 100);
    return {delta, delta_pct: delta_pct != null ? Math.round(delta_pct * 10) / 10 : null};
}

type PnlRowEntry = {row: VNextRow; values: Record<string, number>};

function sumRowValues(rows: PnlRowEntry[], monthKey?: string): number {
    return rows.reduce((sum, {values}) => sum + (monthKey ? (values[monthKey] ?? 0) : Object.values(values).reduce((acc, value) => acc + value, 0)), 0);
}

function sumComparisonTotals(rows: PnlRowEntry[], compRowTotals: Record<string, number> | null): number | null {
    if (!compRowTotals) return null;
    return rows.reduce((sum, {row}) => sum + (compRowTotals[row.node_id] ?? 0), 0);
}

function PnlAccountRow({
    row,
    values,
    months,
    onCellClick,
    compTotal,
    variance,
    hasComparison,
    hasVariance,
}: {
    row: VNextRow;
    values: Record<string, number>;
    months: VNextMonth[];
    onCellClick?: (info: XeroPnlCellInfo) => void;
    compTotal: number | null;
    variance?: RowVarianceEntry;
    hasComparison: boolean;
    hasVariance: boolean;
}) {
    const rowTotal = Object.values(values).reduce((s, v) => s + v, 0);
    return (
        <tr key={row.node_id} className="border-b border-gray-100 hover:bg-gray-50/50">
            <td className="px-3 py-1.5 sticky left-0 bg-white truncate max-w-[200px] pl-10" title={row.label}>
                {row.label}
            </td>
            {months.map((m, i) => {
                const val = values[m.month_key] ?? 0;
                return (
                    <td
                        key={m.month_key}
                        className={`text-right px-3 py-1.5 tabular-nums ${
                            onCellClick ? "cursor-pointer hover:bg-[#5BF6C8]/20" : ""
                        } ${val < 0 ? "text-red-600" : ""} ${monthCellClass(m)} ${monthDividerClass(months, i)}`}
                        onClick={() => onCellClick?.({
                            nodeId: row.node_id,
                            label: row.label,
                            monthKey: m.month_key,
                            monthLabel: m.label || m.month_key,
                            value: val,
                            isForecast: m.status === "forecast",
                            accountCode: row.xero_account_id || row.node_id,
                        })}
                    >
                        {val !== 0 ? fmtCurrency(val) : "—"}
                    </td>
                );
            })}
            <td className={`text-right px-3 py-1.5 font-medium tabular-nums ${rowTotal < 0 ? "text-red-600" : ""}`}>
                {fmtCurrency(rowTotal)}
            </td>
            {hasComparison && (
                <td className={`text-right px-3 py-1.5 tabular-nums bg-gray-50/50 ${compTotal != null && compTotal < 0 ? "text-red-600" : ""}`}>
                    {compTotal != null ? fmtCurrency(compTotal) : "—"}
                </td>
            )}
            {hasVariance && <VarianceCells entry={variance} />}
        </tr>
    );
}

function SectionBlock({
    label,
    rows,
    months,
    totals,
    onCellClick,
    compRowTotals,
    compSectionTotal,
    sectionTotal,
    rowVariance,
    sectionKey,
    hasVariance,
    hasComparison,
    expanded,
    onToggle,
}: {
    label: string;
    rows: PnlRowEntry[];
    months: VNextMonth[];
    totals: Record<string, number>;
    onCellClick?: (info: XeroPnlCellInfo) => void;
    compRowTotals: Record<string, number> | null;
    compSectionTotal: number | null;
    sectionTotal: number;
    rowVariance?: Record<string, RowVarianceEntry> | null;
    sectionKey: string;
    hasVariance: boolean;
    hasComparison: boolean;
    expanded: Set<string>;
    onToggle: (key: string) => void;
}) {
    const supportsMappedGrouping = sectionKey === "costs" || sectionKey === "expenses";
    const groupedRows = supportsMappedGrouping
        ? rows.reduce((acc, entry) => {
            const parentGroup = entry.row.parent_group?.trim() || "Ungrouped";
            const accountGroup = entry.row.account_group?.trim() || parentGroup;
            if (!acc.has(parentGroup)) acc.set(parentGroup, new Map());
            const accountGroups = acc.get(parentGroup)!;
            if (!accountGroups.has(accountGroup)) accountGroups.set(accountGroup, []);
            accountGroups.get(accountGroup)!.push(entry);
            return acc;
        }, new Map<string, Map<string, PnlRowEntry[]>>())
        : new Map<string, Map<string, PnlRowEntry[]>>();

    return (
        <>
            {/* Section header */}
            <tr className="bg-gray-50">
                <td colSpan={months.length + 2 + (hasComparison ? 1 : 0) + (hasVariance ? 2 : 0)} className="px-3 py-1.5 font-bold text-xs uppercase tracking-wider text-[#209DB3]">
                    {label}
                </td>
            </tr>
            {/* Flat sections: show individual rows only when expanded */}
            {/* Rows */}
            {supportsMappedGrouping && groupedRows.size > 0 ? (
                Array.from(groupedRows.entries()).map(([parentGroup, accountGroups]) => {
                    const pgKey = `pg:${sectionKey}:${parentGroup}`;
                    const isOpen = expanded.has(pgKey);
                    const parentRows = Array.from(accountGroups.values()).flat();
                    const parentCompTotal = sumComparisonTotals(parentRows, compRowTotals);
                    const parentTotal = sumRowValues(parentRows);
                    const parentVariance = hasComparison && parentCompTotal != null
                        ? computeVariance(parentTotal, parentCompTotal)
                        : undefined;
                    const Chevron = isOpen ? ChevronDownIcon : ChevronRightIcon;
                    return (
                        <Fragment key={`pg-block:${sectionKey}:${parentGroup}`}>
                            <tr className="bg-gray-50/50 cursor-pointer select-none" onClick={() => onToggle(pgKey)}>
                                <td className="px-3 py-1.5 sticky left-0 bg-gray-50 z-20 font-semibold">
                                    <span className="inline-flex items-center gap-1">
                                        <Chevron className="size-3.5 text-muted-foreground" />
                                        {parentGroup}
                                    </span>
                                </td>
                                {months.map((m, i) => {
                                    const value = sumRowValues(parentRows, m.month_key);
                                    return (
                                        <td key={m.month_key} className={`text-right px-3 py-1.5 font-semibold tabular-nums ${value < 0 ? "text-red-600" : ""} ${monthCellClass(m)} ${monthDividerClass(months, i)}`}>
                                            {value !== 0 ? fmtCurrency(value) : "—"}
                                        </td>
                                    );
                                })}
                                <td className={`text-right px-3 py-1.5 font-semibold tabular-nums ${parentTotal < 0 ? "text-red-600" : ""}`}>
                                    {fmtCurrency(parentTotal)}
                                </td>
                                {hasComparison && (
                                    <td className={`text-right px-3 py-1.5 font-semibold tabular-nums bg-gray-50/50 ${parentCompTotal != null && parentCompTotal < 0 ? "text-red-600" : ""}`}>
                                        {parentCompTotal != null ? fmtCurrency(parentCompTotal) : "—"}
                                    </td>
                                )}
                                {hasVariance && <VarianceCells entry={parentVariance} />}
                            </tr>
                            {isOpen && Array.from(accountGroups.entries()).map(([accountGroup, groupRows]) => {
                                const showAccountGroup = accountGroup !== parentGroup;
                                const accountGroupCompTotal = sumComparisonTotals(groupRows, compRowTotals);
                                const accountGroupTotal = sumRowValues(groupRows);
                                const accountGroupVariance = hasComparison && accountGroupCompTotal != null
                                    ? computeVariance(accountGroupTotal, accountGroupCompTotal)
                                    : undefined;
                                return (
                                    <Fragment key={`ag-block:${sectionKey}:${parentGroup}:${accountGroup}`}>
                                        {showAccountGroup && (
                                            <tr className="bg-white">
                                                <td className="px-3 py-1.5 sticky left-0 bg-white text-muted-foreground font-medium pl-8">
                                                    {accountGroup}
                                                </td>
                                                {months.map((m, i) => {
                                                    const value = sumRowValues(groupRows, m.month_key);
                                                    return (
                                                        <td key={m.month_key} className={`text-right px-3 py-1.5 font-medium tabular-nums ${value < 0 ? "text-red-600" : ""} ${monthCellClass(m)} ${monthDividerClass(months, i)}`}>
                                                            {value !== 0 ? fmtCurrency(value) : "—"}
                                                        </td>
                                                    );
                                                })}
                                                <td className={`text-right px-3 py-1.5 font-medium tabular-nums ${accountGroupTotal < 0 ? "text-red-600" : ""}`}>
                                                    {fmtCurrency(accountGroupTotal)}
                                                </td>
                                                {hasComparison && (
                                                    <td className={`text-right px-3 py-1.5 font-medium tabular-nums bg-gray-50/50 ${accountGroupCompTotal != null && accountGroupCompTotal < 0 ? "text-red-600" : ""}`}>
                                                        {accountGroupCompTotal != null ? fmtCurrency(accountGroupCompTotal) : "—"}
                                                    </td>
                                                )}
                                                {hasVariance && <VarianceCells entry={accountGroupVariance} />}
                                            </tr>
                                        )}
                                        {groupRows.map(({row, values}) => {
                                            const compTotal = compRowTotals?.[row.node_id] ?? null;
                                            const variance = rowVariance?.[row.node_id] ?? (hasComparison && compTotal != null ? computeVariance(Object.values(values).reduce((s, v) => s + v, 0), compTotal) : undefined);
                                            return (
                                                <PnlAccountRow
                                                    key={row.node_id}
                                                    row={row}
                                                    values={values}
                                                    months={months}
                                                    onCellClick={onCellClick}
                                                    compTotal={compTotal}
                                                    variance={variance}
                                                    hasComparison={hasComparison}
                                                    hasVariance={hasVariance}
                                                />
                                            );
                                        })}
                                    </Fragment>
                                );
                            })}
                        </Fragment>
                    );
                })
            ) : (
                <>
                    {rows.length > 1 && !expanded.has(`sec:${sectionKey}`) ? (
                        <tr className="border-b border-gray-100 cursor-pointer select-none hover:bg-gray-50/50" onClick={() => onToggle(`sec:${sectionKey}`)}>
                            <td className="px-3 py-1.5 sticky left-0 bg-white text-muted-foreground text-[11px]">
                                <span className="inline-flex items-center gap-1">
                                    <ChevronRightIcon className="size-3.5" />
                                    {rows.length} accounts
                                </span>
                            </td>
                            <td colSpan={months.length + 1 + (hasComparison ? 1 : 0) + (hasVariance ? 2 : 0)} />
                        </tr>
                    ) : rows.map(({row, values}) => {
                        const compTotal = compRowTotals?.[row.node_id] ?? null;
                        const rowTotal = Object.values(values).reduce((s, v) => s + v, 0);
                        const variance = rowVariance?.[row.node_id] ?? (hasComparison && compTotal != null ? computeVariance(rowTotal, compTotal) : undefined);
                        return (
                            <PnlAccountRow
                                key={row.node_id}
                                row={row}
                                values={values}
                                months={months}
                                onCellClick={onCellClick}
                                compTotal={compTotal}
                                variance={variance}
                                hasComparison={hasComparison}
                                hasVariance={hasVariance}
                            />
                        );
                    })}
                    {expanded.has(`sec:${sectionKey}`) && rows.length > 1 && (
                        <tr className="border-b border-gray-100 cursor-pointer select-none hover:bg-gray-50/50" onClick={() => onToggle(`sec:${sectionKey}`)}>
                            <td className="px-3 py-1 sticky left-0 bg-white text-muted-foreground text-[11px]" colSpan={months.length + 2 + (hasComparison ? 1 : 0) + (hasVariance ? 2 : 0)}>
                                <span className="inline-flex items-center gap-1">
                                    <ChevronDownIcon className="size-3.5" />
                                    Collapse
                                </span>
                            </td>
                        </tr>
                    )}
                </>
            )}
            {/* Section subtotal */}
            <tr className="border-b-2 border-gray-200 bg-gray-50/50">
                <td className="px-3 py-1.5 font-semibold sticky left-0 bg-gray-50 z-20">
                    Total {label}
                </td>
                {months.map((m, i) => {
                    const tv = totals[m.month_key] ?? 0;
                    return (
                        <td key={m.month_key} className={`text-right px-3 py-1.5 font-semibold tabular-nums ${tv < 0 ? "text-red-600" : ""} ${monthCellClass(m)} ${monthDividerClass(months, i)}`}>
                            {fmtCurrency(tv)}
                        </td>
                    );
                })}
                <td className={`text-right px-3 py-1.5 font-bold tabular-nums ${sectionTotal < 0 ? "text-red-600" : ""}`}>
                    {fmtCurrency(sectionTotal)}
                </td>
                {hasComparison && (
                    <td className={`text-right px-3 py-1.5 font-bold tabular-nums bg-gray-50/50 ${compSectionTotal != null && compSectionTotal < 0 ? "text-red-600" : ""}`}>
                        {compSectionTotal != null ? fmtCurrency(compSectionTotal) : "—"}
                    </td>
                )}
                {hasVariance && <VarianceCells entry={rowVariance?.[`section:${sectionKey}`] ?? (hasComparison && compSectionTotal != null ? computeVariance(sectionTotal, compSectionTotal) : undefined)} />}
            </tr>
        </>
    );
}

function SummaryRow({
    label,
    values,
    months,
    className = "",
    compTotal,
    primaryTotal,
    varianceEntry,
    hasVariance,
    hasComparison,
}: {
    label: string;
    values: Record<string, number>;
    months: VNextMonth[];
    className?: string;
    compTotal?: number | null;
    primaryTotal?: number;
    varianceEntry?: RowVarianceEntry;
    hasVariance?: boolean;
    hasComparison?: boolean;
}) {
    const total = primaryTotal ?? Object.values(values).reduce((s, v) => s + v, 0);
    const variance = varianceEntry ?? (hasComparison && compTotal != null ? computeVariance(total, compTotal) : undefined);
    const stickyBg = className.includes("bg-[#5BF6C8]") ? "bg-[#dcfef2]"
        : className.includes("bg-[#209DB3]") ? "bg-[#e8f5f7]"
        : className.includes("bg-amber") ? "bg-amber-50"
        : "bg-white";
    return (
        <tr className={`border-b-2 border-gray-300 ${className}`}>
            <td className={`px-3 py-2 sticky left-0 z-20 ${stickyBg} ${className.includes("font-bold") ? "font-bold" : ""}`}>{label}</td>
            {months.map((m, i) => (
                <td key={m.month_key} className={`text-right px-3 py-2 tabular-nums ${(values[m.month_key] ?? 0) < 0 ? "text-red-600" : ""} ${monthCellClass(m)} ${monthDividerClass(months, i)}`}>
                    {fmtCurrency(values[m.month_key] ?? 0)}
                </td>
            ))}
            <td className={`text-right px-3 py-2 tabular-nums ${total < 0 ? "text-red-600" : ""}`}>
                {fmtCurrency(total)}
            </td>
            {hasComparison && (
                <td className={`text-right px-3 py-2 tabular-nums bg-gray-50/50 ${compTotal != null && compTotal < 0 ? "text-red-600" : ""}`}>
                    {compTotal != null ? fmtCurrency(compTotal) : "—"}
                </td>
            )}
            {hasVariance && <VarianceCells entry={variance} />}
        </tr>
    );
}

function PctRow({
    label,
    values,
    months,
    totalPct,
    hasVariance,
    hasComparison,
}: {
    label: string;
    values: Record<string, number | null>;
    months: VNextMonth[];
    totalPct: number | null;
    hasVariance?: boolean;
    hasComparison?: boolean;
}) {
    return (
        <tr className="border-b border-gray-100">
            <td className="px-3 py-1 sticky left-0 bg-white text-muted-foreground italic text-[11px]">
                {label}
            </td>
            {months.map((m, i) => (
                <td key={m.month_key} className={`text-right px-3 py-1 tabular-nums text-muted-foreground italic text-[11px] ${monthCellClass(m)} ${monthDividerClass(months, i)}`}>
                    {fmtPct(values[m.month_key])}
                </td>
            ))}
            <td className="text-right px-3 py-1 tabular-nums text-muted-foreground italic font-medium text-[11px]">
                {fmtPct(totalPct)}
            </td>
            {hasComparison && <td className="px-3 py-1" />}
            {hasVariance && (
                <>
                    <td className="px-3 py-1" />
                    <td className="px-3 py-1" />
                </>
            )}
        </tr>
    );
}
